import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { GroomingQueueEntry, GroomingQueueState } from "@ttc-spectree/toolbox-contract";
import { groomingKeys } from "@/hooks/queries/use-grooming";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import type { GroomingBatch } from "@/lib/api/grooming";
import { groomingErrorMessage } from "@/lib/grooming/error-messages";
import { useRunnerBridge } from "@/lib/grooming/runner-bridge";

/**
 * Starting and stopping one batch's run in SpecTree Toolbox (PKG-PM-C,
 * ER-1638, feature PI-SPEC-SW-1492; extracted from `batch-workspace.tsx`
 * unchanged), so the Session screen's "Prepare N briefs" starts the run in
 * place (frame C4) with exactly the run step's Start, Stop and Resume.
 *
 * In a contract-3 app (epic PI-SPEC-SW-E284) Start adds the batch to the
 * app's run queue (`enqueueBatch`) and Stop stops this batch's running
 * entries and takes its waiting ones off the queue (other sessions' runs go
 * on). A contract-2 app keeps `runBatch` and `stopBatch`. After a start or a
 * stop the batch is read again, and the project's session and batch list too,
 * so the Session screen moves from the draft to the run.
 *
 * Runner events (live item states, the stop detail, "Briefs ready") stay with
 * the page that shows the run (`BatchWorkspace`, `SessionPrepared`'s parent).
 */

/** This batch's entries in the run queue: running now and waiting in Up next. Pure; exported for tests. */
export function batchQueueEntries(
  state: GroomingQueueState | null,
  batchId: string
): { running: GroomingQueueEntry[]; upNext: GroomingQueueEntry[] } {
  if (!state) return { running: [], upNext: [] };
  return {
    running: state.running.filter((entry) => entry.batchId === batchId),
    upNext: state.upNext.filter((entry) => entry.batchId === batchId),
  };
}

export interface BatchRun {
  /** Start (or resume) the run; resolves true when the app accepted it. A no-op (false) without a runner. */
  start: () => Promise<boolean>;
  /** Stop this batch's run. */
  stop: () => Promise<void>;
  /** True while a start or stop waits for the app. */
  busy: boolean;
  /** True from a start (or a heard item-started) until a stop or finish has been read. */
  liveRun: boolean;
  setLiveRun: Dispatch<SetStateAction<boolean>>;
  /** What went wrong starting or stopping, in plain words; null otherwise. */
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  /** This batch's entries in the run queue (empty outside queue mode). */
  queueEntries: { running: GroomingQueueEntry[]; upNext: GroomingQueueEntry[] };
  /** True in a contract-3 app with a runner: runs go through the run queue. */
  queueMode: boolean;
  /** The run queue's state in queue mode, else null. */
  queueState: GroomingQueueState | null;
  /** True when this batch is running or has entries in the queue. */
  active: boolean;
}

/**
 * Start and stop `batch`'s run. `onStart` runs before each start (the run
 * step clears its stop banner there). Pass no batch for a page without one:
 * start and stop then do nothing.
 */
export function useBatchRun(
  project: string,
  batch: Pick<GroomingBatch, "id" | "status"> | null,
  options: { onStart?: () => void } = {}
): BatchRun {
  const { runner } = useRunnerBridge();
  const queryClient = useQueryClient();
  const { supported: queueSupported, state: queueState, actions: queueActions } = useGroomingQueue();
  const [liveRun, setLiveRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onStartRef = useRef(options.onStart);
  onStartRef.current = options.onStart;

  const batchId = batch?.id ?? null;
  // A run read as no longer running (stopped, completed, canceled) ends the live run for every
  // caller, so Resume is usable again on pages that hear no runner events (Session, frame C4).
  const status = batch?.status ?? null;
  const lastStatus = useRef(status);
  useEffect(() => {
    const was = lastStatus.current;
    lastStatus.current = status;
    if (was === "running" && status !== null && status !== "running" && status !== "draft") setLiveRun(false);
  }, [status]);
  const queueMode = queueSupported && runner !== null;
  const queueEntries = batchQueueEntries(queueMode ? queueState : null, batchId ?? "");

  const readBatch = useCallback(() => {
    if (!batchId) return;
    void queryClient.invalidateQueries({ queryKey: groomingKeys.batch(batchId), exact: true });
    void queryClient.invalidateQueries({ queryKey: groomingKeys.session(project) });
    void queryClient.invalidateQueries({ queryKey: groomingKeys.batches(project) });
  }, [queryClient, batchId, project]);

  const start = useCallback((): Promise<boolean> => {
    if (!runner || !batchId) return Promise.resolve(false);
    onStartRef.current?.();
    setError(null);
    setLiveRun(true);
    setBusy(true);
    const finish = () => {
      setBusy(false);
      readBatch();
    };
    if (queueMode) {
      return queueActions
        .enqueueBatch(batchId)
        .then((result) => {
          if (!result.ok) {
            setError(result.message);
            setLiveRun(false);
            return false;
          }
          return true;
        })
        .finally(finish);
    }
    return runner
      .runBatch(batchId)
      .then(
        (result) => {
          if (!result.ok) {
            setError(groomingErrorMessage(result.errorCode).cause);
            setLiveRun(false);
            return false;
          }
          return true;
        },
        () => {
          setError("SpecTree Toolbox could not start the run.");
          setLiveRun(false);
          return false;
        }
      )
      .finally(finish);
  }, [runner, batchId, readBatch, queueMode, queueActions]);

  const { running: runningEntries, upNext: waitingEntries } = queueEntries;
  const stop = useCallback((): Promise<void> => {
    if (!runner || !batchId) return Promise.resolve();
    setBusy(true);
    if (queueMode) {
      // E284: stop this session's running entries and take its waiting ones off the queue; other runs go on.
      return Promise.all([
        ...runningEntries.map((entry) => queueActions.stop(entry.id)),
        ...waitingEntries.map((entry) => queueActions.remove(entry.id)),
      ])
        .then((results) => {
          for (const result of results) {
            if (!result.ok) {
              setError(result.message);
              break;
            }
          }
        })
        .finally(() => {
          setBusy(false);
          readBatch();
        });
    }
    return runner
      .stopBatch(batchId)
      .then(
        () => undefined,
        () => {
          setError("SpecTree Toolbox could not stop the run.");
        }
      )
      .finally(() => {
        setBusy(false);
      });
  }, [runner, batchId, queueMode, queueActions, runningEntries, waitingEntries, readBatch]);

  return {
    start,
    stop,
    busy,
    liveRun,
    setLiveRun,
    error,
    setError,
    queueEntries,
    queueMode,
    queueState: queueMode ? queueState : null,
    active: batch?.status === "running" || queueEntries.running.length + queueEntries.upNext.length > 0,
  };
}
